import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeftIcon,
  SparklesIcon,
  PlayIcon,
  PlusIcon,
  MoreVerticalIcon,
  ClockIcon,
  PowerIcon,
  MailIcon,
  MessageSquareIcon,
  ActivityIcon,
  MoonIcon,
  SunIcon,
  FlameIcon,
  AlertTriangleIcon,
  HomeIcon,
  Edit2Icon,
  Trash2Icon,
  Loader2Icon,
  CheckCircle2Icon
} from 'lucide-react';
import { SceneRule } from '../types/scene';
import { sceneService } from '../services/sceneService';
import { Sensor } from './FloorPlanHeatMap';
import { SceneEditorModal } from './SceneEditorModal';

interface SceneListViewProps {
  apartmentAssetId: string | null;
  allSensors: Sensor[];
  rooms?: any[];
  selectedRoomId?: string;
  apartmentLabel?: string;
  onShowToast: (message: string, type?: 'success' | 'info' | 'error') => void;
  onBack?: () => void;
  onOpenEditor?: (scene: SceneRule | null, defaultZoneId?: string) => void;
}

const ICON_MAP: Record<string, any> = {
  Sparkles: SparklesIcon,
  Moon: MoonIcon,
  Sun: SunIcon,
  Flame: FlameIcon,
  AlertTriangle: AlertTriangleIcon,
  Home: HomeIcon,
};

export function SceneListView({
  apartmentAssetId,
  allSensors,
  rooms = [],
  selectedRoomId = 'ALL',
  apartmentLabel = '전체공간',
  onShowToast,
  onBack,
  onOpenEditor
}: SceneListViewProps) {
  const [scenes, setScenes] = useState<SceneRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [executingSceneId, setExecutingSceneId] = useState<string | null>(null);

  // Editor Modal State
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingScene, setEditingScene] = useState<SceneRule | null>(null);

  // Active Menu Dropdown state
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  // Load Scenes
  useEffect(() => {
    async function load() {
      if (!apartmentAssetId) return;
      setLoading(true);
      const data = await sceneService.getScenes(apartmentAssetId);
      setScenes(data);
      setLoading(false);
    }
    load();
  }, [apartmentAssetId]);

  // Filter scenes based on selectedRoomId
  const filteredScenes = useMemo(() => {
    const safeScenes = Array.isArray(scenes) ? scenes : [];
    if (!selectedRoomId || selectedRoomId === 'ALL') {
      return safeScenes;
    }
    return safeScenes.filter(s => {
      if (!s) return false;
      // When specific zone is selected, strictly show ONLY that zone's scenes
      return s.targetScope === 'zone' && s.zoneId === selectedRoomId;
    });
  }, [scenes, selectedRoomId]);

  // Toggle Enable
  const handleToggleEnable = async (sceneId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const target = scenes.find((s) => s.id === sceneId);
    if (!target) return;
    const updatedTarget = { ...target, enabled: !target.enabled };
    const updated = scenes.map((s) => (s.id === sceneId ? updatedTarget : s));
    setScenes(updated);
    const res = await sceneService.saveScene(updatedTarget, apartmentAssetId);
    onShowToast(res.message, res.success ? 'info' : 'error');
  };

  // Run Scene Now
  const handleRunScene = async (scene: SceneRule, e: React.MouseEvent) => {
    e.stopPropagation();
    setExecutingSceneId(scene.id);
    const result = await sceneService.executeScene(scene);
    setExecutingSceneId(null);
    onShowToast(result.message, result.success ? 'success' : 'error');
  };

  // Delete Scene
  const handleDeleteScene = async (sceneId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = scenes.filter((s) => s.id !== sceneId);
    setScenes(updated);
    setActiveMenuId(null);
    await sceneService.deleteScene(sceneId, apartmentAssetId);
    onShowToast('시나리오가 삭제되었습니다.', 'info');
  };

  // Save (Create or Update)
  const handleSaveScene = async (savedScene: SceneRule) => {
    let updated: SceneRule[];
    const exists = scenes.some((s) => s.id === savedScene.id);
    if (exists) {
      updated = scenes.map((s) => (s.id === savedScene.id ? savedScene : s));
    } else {
      updated = [savedScene, ...scenes];
    }
    setScenes(updated);
    const res = await sceneService.saveScene(savedScene, apartmentAssetId);
    onShowToast(res.message, res.success ? 'success' : 'error');
    setIsEditorOpen(false);
    setEditingScene(null);
  };

  const currentTitle = isEditorOpen
    ? (editingScene ? '시나리오 수정' : '새 시나리오 만들기')
    : '스마트 시나리오';

  const handleHeaderBack = () => {
    if (isEditorOpen) {
      setIsEditorOpen(false);
      setEditingScene(null);
    } else if (onBack) {
      onBack();
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden select-none" style={{ background: 'var(--mobile-bg-alt)' }}>


      {/* ─── Scrollable Content ─── */}
      <div
        className="flex-1 overflow-y-auto px-4 pt-3 pb-24 space-y-3"
        style={{ background: 'var(--mobile-bg-alt)' }}
      >
      {/* Top Banner & Summary */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-panel rounded-2xl p-4 flex items-center justify-between border border-cyan-500/20 shadow-lg"
      >
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-cyan-500/20 text-cyan-400">
            <SparklesIcon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">스마트 시나리오 / 자동화</h3>
            <p className="text-[0.72rem] text-slate-400">
              시간 스케줄 및 센서 조건에 따라 기기를 자동 제어합니다.
            </p>
          </div>
        </div>

        <button
          onClick={() => {
            if (onOpenEditor) {
              onOpenEditor(null, selectedRoomId);
            } else {
              setEditingScene(null);
              setIsEditorOpen(true);
            }
          }}
          className="w-9 h-9 rounded-xl bg-cyan-500 text-black flex items-center justify-center flex-shrink-0 shadow-md shadow-cyan-500/20 active:scale-90 transition-transform"
          title="시나리오 추가"
        >
          <PlusIcon className="w-5 h-5 stroke-[3]" />
        </button>
      </motion.div>

      {/* Scene Cards List */}
      <div className="space-y-3">
        {loading ? (
          <div className="py-16 flex flex-col items-center justify-center gap-2 text-slate-500">
            <Loader2Icon className="w-6 h-6 animate-spin text-cyan-400" />
            <span className="text-xs">시나리오를 불러오는 중...</span>
          </div>
        ) : filteredScenes.length === 0 ? (
          <div className="py-16 flex flex-col items-center justify-center gap-3 text-slate-500">
            <SparklesIcon className="w-10 h-10 opacity-30" />
            <span className="text-xs">등록된 스마트 시나리오가 없습니다</span>
            <button
              onClick={() => {
                if (onOpenEditor) {
                  onOpenEditor(null);
                } else {
                  setEditingScene(null);
                  setIsEditorOpen(true);
                }
              }}
              className="px-4 py-2 rounded-xl bg-cyan-500/20 text-cyan-400 text-xs font-bold border border-cyan-400/30"
            >
              첫 시나리오 만들기
            </button>
          </div>
        ) : (
          filteredScenes.map((scene, i) => {
            const IconCmp = ICON_MAP[scene.icon || 'Sparkles'] || SparklesIcon;
            const isExecuting = executingSceneId === scene.id;

            return (
              <motion.div
                key={scene.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 + i * 0.03 }}
                onClick={() => {
                  setEditingScene(scene);
                  setIsEditorOpen(true);
                }}
                className={`glass-panel rounded-2xl p-4 border transition-all relative cursor-pointer ${
                  scene.enabled
                    ? 'border-white/10 shadow-md hover:border-cyan-500/30'
                    : 'border-white/5 opacity-60 bg-black/40'
                }`}
              >
                {/* Header: Icon + Name + Enable Switch + Menu */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div
                      className={`p-2 rounded-xl flex-shrink-0 ${
                        scene.enabled ? 'bg-cyan-500/20 text-cyan-400' : 'bg-white/5 text-slate-500'
                      }`}
                    >
                      <IconCmp className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-white truncate flex items-center gap-1.5 flex-wrap">
                        <span className="truncate">{scene.name}</span>
                        <span
                          className={`text-[0.6rem] font-bold px-1.5 py-0.2 rounded-md ${
                            scene.targetScope === 'unit' || !scene.zoneId
                              ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                              : 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                          }`}
                        >
                          {scene.targetScope === 'unit' || !scene.zoneId ? '🏢 전체공간' : `📍 ${scene.zoneName || '공간'}`}
                        </span>

                      </div>
                      {scene.description && (
                        <p className="text-[0.7rem] text-slate-400 truncate mt-0.5">
                          {scene.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {/* Direct Edit Button (연필 아이콘) */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingScene(scene);
                        setIsEditorOpen(true);
                      }}
                      className="p-1.5 rounded-lg bg-white/5 hover:bg-cyan-500/20 text-slate-400 hover:text-cyan-300 transition-colors active:scale-95"
                      title="스케줄 편집"
                      aria-label="편집"
                    >
                      <Edit2Icon className="w-3.5 h-3.5" />
                    </button>

                    {/* Direct Delete Button (휴지통 아이콘) */}
                    <button
                      onClick={(e) => handleDeleteScene(scene.id, e)}
                      className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 text-slate-400 hover:text-red-300 transition-colors active:scale-95"
                      title="스케줄 삭제"
                      aria-label="삭제"
                    >
                      <Trash2Icon className="w-3.5 h-3.5" />
                    </button>

                    {/* Active Toggle Switch */}
                    <button
                      type="button"
                      onClick={(e) => handleToggleEnable(scene.id, e)}
                      className={`w-12 h-6.5 rounded-full p-0.5 transition-all duration-200 relative flex items-center flex-shrink-0 shadow-inner active:scale-95 ${
                        scene.enabled
                          ? 'bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.4)]'
                          : 'bg-slate-700 hover:bg-slate-600'
                      }`}
                      style={{ height: '26px', width: '48px' }}
                      aria-label="시나리오 활성화 토글"
                      title="스케줄 On/Off"
                    >
                      <motion.div
                        className="rounded-full bg-white shadow-md flex-shrink-0"
                        style={{ width: '22px', height: '22px' }}
                        animate={{ x: scene.enabled ? 22 : 0 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      />
                    </button>
                  </div>
                </div>

                {/* Badges: Conditions & Actions Summary */}
                <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap items-center gap-1.5">
                  {/* Conditions Badges */}
                  {scene.conditions.map((c) => (
                    <span
                      key={c.id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/5 border border-white/10 text-[0.68rem] text-slate-300"
                    >
                      {c.type === 'schedule' ? (
                        <>
                          <ClockIcon className="w-3 h-3 text-indigo-400" />
                          <span>{c.schedule?.time}</span>
                        </>
                      ) : (
                        <>
                          <ActivityIcon className="w-3 h-3 text-indigo-400" />
                          <span>
                            {c.sensor?.key} {c.sensor?.operator} {c.sensor?.value}
                          </span>
                        </>
                      )}
                    </span>
                  ))}

                  <span className="text-[0.65rem] text-slate-500">➔</span>

                  {/* Actions Badges */}
                  {scene.actions.map((a) => (
                    <span
                      key={a.id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-[0.68rem] text-emerald-300"
                    >
                      {a.type === 'device_control' ? (
                        <>
                          <PowerIcon className="w-3 h-3 text-emerald-400" />
                          <span>
                            {a.deviceControl?.deviceNames.length}개 기기{' '}
                            {a.deviceControl?.command === 'turn_on'
                              ? '켜기'
                              : a.deviceControl?.command === 'turn_off'
                              ? '끄기'
                              : '모드'}
                          </span>
                        </>
                      ) : a.type === 'send_email' ? (
                        <>
                          <MailIcon className="w-3 h-3 text-cyan-400" />
                          <span>이메일</span>
                        </>
                      ) : (
                        <>
                          <MessageSquareIcon className="w-3 h-3 text-cyan-400" />
                          <span>문자</span>
                        </>
                      )}
                    </span>
                  ))}
                </div>

                {/* Bottom: Run Now Button */}
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-[0.65rem] text-slate-500 font-mono">
                    {scene.lastTriggered
                      ? `최근 실행: ${new Date(scene.lastTriggered).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}`
                      : '실행 이력 없음'}
                  </span>

                  <button
                    disabled={isExecuting}
                    onClick={(e) => handleRunScene(scene, e)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-cyan-500/20 text-cyan-300 text-xs font-bold border border-cyan-400/30 active:scale-95 transition-all shadow-sm"
                  >
                    {isExecuting ? (
                      <Loader2Icon className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <PlayIcon className="w-3.5 h-3.5 fill-current" />
                    )}
                    <span>지금 실행</span>
                  </button>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Scene Editor Fullscreen Modal */}
      <SceneEditorModal
        isOpen={isEditorOpen}
        scene={editingScene}
        allSensors={allSensors}
        rooms={rooms}
        defaultRoomId={selectedRoomId}
        apartmentLabel={apartmentLabel}
        onClose={() => {
          setIsEditorOpen(false);
          setEditingScene(null);
        }}
        onSave={handleSaveScene}
      />
      </div>
    </div>
  );
}
